import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useProfile } from "../../../hooks/useProfile";
import { ThemeContext } from "../../../constant/theme";
import { LanguageContext } from "../../../constant/LanguageContext";
import { translations_admin } from "../../../constant/Admin_Translations";
import { hasAdminLevel } from "../utils/adminLevelUtils";
import { getNavItems } from "../config/userProfileConfig";

export const useUserProfileState = () => {
  // ============ HOOKS AND STATE ============
  const { user, logout } = useAuth();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const { theme, setTheme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);

  const isRTL = language === 'ar';
  const t = translations_admin[language];

  // State management
  const [notifications, setNotifications] = useState(3);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(true);

  // View type state - admin or manager
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem('viewType') || 'manager';
  });

  // حفظ الصفحة الحالية
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem('adminUserProfilePage');
    return savedPage || 'professionalProfile';
  });

  useEffect(() => {
    localStorage.setItem('adminUserProfilePage', currentPage);
  }, [currentPage]);

  useEffect(() => {
    // Sync currentPage with URL path
    const navItems = getNavItems(t);
    const pathParts = routerLocation.pathname.split('/').filter(Boolean);
    const lastPart = pathParts[pathParts.length - 1];

    if (lastPart) {
      const matchedNavItem = navItems.find(
        (item) => item.page.toLowerCase() === lastPart.toLowerCase()
      );

      if (matchedNavItem) {
        if (currentPage !== matchedNavItem.page) {
          setCurrentPage(matchedNavItem.page);
        }
      } else {
        // Fallback to professionalProfile if path is unhandled or root
        if (currentPage !== 'professionalProfile') {
          setCurrentPage('professionalProfile');
        }
      }
    } else {
      if (currentPage !== 'professionalProfile') {
        setCurrentPage('professionalProfile');
      }
    }
  }, [routerLocation.pathname, t, currentPage]);

  // ============ EVENT HANDLERS ============
  const handleViewTypeChange = (type) => {
    setViewType(type);
    // storage of viewType is centralized in ViewTypeSwitcher

    // Navigation logic based on view type
    if (type === 'user') {
      // Check if user has admin level - if yes, stay on userprofileadmin, otherwise go to userprofilemanager
      if (hasAdminLevel()) {
        // Stay on current page (userprofileadmin)
        setCurrentPage('professionalProfile');
      } else {
        navigate('/userprofilemanager/professionalProfile');
      }
    } else if (type === 'manager') {
      navigate('/dashboard/dashboard');
    } else if (type === 'admin') {
      navigate('/dashboard/dashboard');
    }
  };

  const handleLogout = () => {
    try {
      logout();
      try {
        localStorage.removeItem('nav_admin');
        localStorage.removeItem('nav_manager');
        localStorage.removeItem('nav_employee');
      } catch (e) { }
      navigate("/login");
    } catch (error) {
      console.error(t.logoutError, error);
      navigate("/login");
    }
  };

  const handleSwitchManager = () => {
    try {
      localStorage.removeItem('adminUserProfilePage');
      navigate("/dashboard/dashboard");
    } catch (error) {
      console.error(t.switchError, error);
      navigate("/userprofileadmin");
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    navigate(`/userprofileadmin/${page}`);
  };

  const toggleTheme = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (setTheme) {
      setTheme(newDarkMode ? "dark" : "light");
    }
  };

  return {
    user,
    profile,
    theme,
    setTheme,
    language,
    isRTL,
    t,
    notifications,
    setNotifications,
    sidebarOpen,
    setSidebarOpen,
    isDarkMode,
    setIsDarkMode,
    viewType,
    setViewType,
    currentPage,
    setCurrentPage,
    handleViewTypeChange,
    handleLogout,
    handleSwitchManager,
    handlePageChange,
    toggleTheme,
  };
};
